import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import z from "zod";
import { auth } from "../better-auth/auth";
import { db } from "~/db";
import {
  file,
  location,
  locationRoom,
  locationsToFiles,
  watchList,
} from "~/db/schema";
import { and, asc, desc, eq, inArray, sql } from "drizzle-orm";
import { LocationWithImages } from "./get-locations";
import { fileCategory } from "~/db/schema/localground/fileCategory.schema";
import { filesToCategories } from "~/db/schema/localground/filesToCategories.schema";

export const addLocationToWatchList = createServerFn({ method: "POST" })
  .inputValidator(z.object({ locationId: z.number() }))
  .handler(async (ctx) => {
    const { locationId } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    await db.insert(watchList).values({
      locationId,
      userId: userSession.user.id,
    });

    return { success: true };
  });

export const removeLocationFromWatchList = createServerFn({ method: "POST" })
  .inputValidator(z.object({ locationId: z.number() }))
  .handler(async (ctx) => {
    const { locationId } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    await db
      .delete(watchList)
      .where(
        and(
          eq(watchList.locationId, locationId),
          eq(watchList.userId, userSession.user.id),
        ),
      );

    return { success: true };
  });

export const getLocationFromWatchList = createServerFn({ method: "GET" })
  .inputValidator(z.object({ locationId: z.number() }))
  .handler(async (ctx) => {
    const { locationId } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    const watchListEntry = await db
      .select()
      .from(watchList)
      .where(
        and(
          eq(watchList.locationId, locationId),
          eq(watchList.userId, userSession.user.id),
        ),
      );

    if (watchListEntry.length === 0) {
      return null;
    }

    const loc = await db.query.location.findFirst({
      where: eq(location.id, locationId),
    });

    if (!location) {
      return null;
    }

    return loc;
  });

export const checkIfLocationsAreInWatchList = createServerFn({ method: "GET" })
  .inputValidator(z.object({ locationIds: z.array(z.number()) }))
  .handler(async (ctx) => {
    const { locationIds } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    const watchListEntries = await db
      .select()
      .from(watchList)
      .where(
        and(
          inArray(watchList.locationId, locationIds),
          eq(watchList.userId, userSession.user.id),
        ),
      );

    return watchListEntries.map((entry) => entry.locationId);
  });

export const getAllWatchList = createServerFn({ method: "GET" }).handler(
  async () => {
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    const watchListEntries = await db
      .select()
      .from(watchList)
      .where(eq(watchList.userId, userSession.user.id))
      .orderBy(desc(watchList.createdAt))
      .execute();

    const locationIds = watchListEntries.map((entry) => entry.locationId);

    if (locationIds.length === 0) {
      return [];
    }

    // include location images
    const imageRows = await db
      .select({
        location,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
        },
      })
      .from(location)
      .leftJoin(locationsToFiles, eq(locationsToFiles.locationId, location.id))
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(inArray(location.id, locationIds))
      .orderBy(asc(file.sort))
      .execute();

    // Fetch rooms for these locations
    const roomRows = await db
      .select({
        locationId: locationRoom.locationId,
        room: {
          id: locationRoom.id,
          name: locationRoom.name,
        },
      })
      .from(locationRoom)
      .where(inArray(locationRoom.locationId, locationIds))
      .orderBy(locationRoom.name)
      .execute();

    // build the locations map with all related data
    const locationsMap = new Map<number, LocationWithImages>();

    for (const row of imageRows) {
      if (!locationsMap.has(row.location.id)) {
        locationsMap.set(row.location.id, {
          ...row.location,
          images: [],
          eventTypes: [],
          locationTypes: [],
          rooms: [],
        });
      }
      if (row.image?.id) {
        locationsMap.get(row.location.id)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    // Add rooms
    for (const row of roomRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.room?.id) {
        loc.rooms!.push(row.room);
      }
    }

    return Array.from(locationsMap.values());
  },
);

export const getPaginatedWatchList = createServerFn({ method: "GET" })
  .inputValidator(z.object({ page: z.number(), pageSize: z.number() }))
  .handler(async (ctx) => {
    const { page, pageSize } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return null;
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return null;
    }

    const countResult = await db
      .select({ count: sql<number>`count(*)` })
      .from(watchList)
      .where(eq(watchList.userId, userSession.user.id))
      .execute();
    const total = countResult[0]?.count ?? 0;

    const watchListEntries = await db
      .select()
      .from(watchList)
      .where(eq(watchList.userId, userSession.user.id))
      .orderBy(desc(watchList.createdAt))
      .limit(pageSize)
      .offset((page - 1) * pageSize)
      .execute();

    const locationIds = watchListEntries.map((entry) => entry.locationId);

    // include location images
    const imageRows = await db
      .select({
        location,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
        },
      })
      .from(location)
      .leftJoin(locationsToFiles, eq(locationsToFiles.locationId, location.id))
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(inArray(location.id, locationIds))
      .orderBy(asc(file.sort))
      .execute();

    // build the locations map with all related data
    const locationsMap = new Map<number, LocationWithImages>();

    for (const row of imageRows) {
      if (!locationsMap.has(row.location.id)) {
        locationsMap.set(row.location.id, {
          ...row.location,
          images: [],
          eventTypes: [],
          locationTypes: [],
        });
      }
      if (row.image?.id) {
        locationsMap.get(row.location.id)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    return {
      locations: Array.from(locationsMap.values()),
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    };
  });

/**
 * Sync localStorage watchlist items to database when user logs in
 */
export const syncWatchlistFromStorage = createServerFn({ method: "POST" })
  .inputValidator(z.object({ locationIds: z.array(z.number()) }))
  .handler(async (ctx) => {
    const { locationIds } = ctx.data;
    const request = getRequest();

    if (!request?.headers) {
      return { success: false, error: "Request headers not available" };
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    if (!userSession) {
      return { success: false, error: "User not authenticated" };
    }

    if (locationIds.length === 0) {
      return { success: true, synced: 0 };
    }

    // Get existing watchlist entries for this user
    const existingEntries = await db
      .select()
      .from(watchList)
      .where(eq(watchList.userId, userSession.user.id))
      .execute();

    const existingLocationIds = new Set(
      existingEntries.map((entry) => entry.locationId),
    );

    // Filter out locations that are already in the watchlist
    const newLocationIds = locationIds.filter(
      (id) => !existingLocationIds.has(id),
    );

    if (newLocationIds.length === 0) {
      return { success: true, synced: 0 };
    }

    // Insert new watchlist entries
    await db.insert(watchList).values(
      newLocationIds.map((locationId) => ({
        locationId,
        userId: userSession.user.id,
      })),
    );

    return { success: true, synced: newLocationIds.length };
  });
